import { useState, useEffect, useCallback } from 'react';
import { useQuery } from 'react-query';
import { searchAPI } from '../services/api';
import useAppStore from '../store/useAppStore';

// Search suggestions data
const searchSuggestions = [
  'headache', 'fever', 'chest pain', 'back pain', 'cough', 'rash',
  'stomach ache', 'dizziness', 'fatigue', 'nausea', 'sore throat',
  'cardiology', 'dermatology', 'pediatrics', 'general medicine',
  'orthopedics', 'neurology', 'psychiatry', 'gynecology'
];

// Symptom to specialty mapping
const symptomToSpecialty = {
  'headache': 'neurology',
  'chest pain': 'cardiology',
  'back pain': 'orthopedics',
  'rash': 'dermatology',
  'cough': 'general medicine',
  'fever': 'general medicine',
  'stomach ache': 'gastroenterology',
  'dizziness': 'neurology',
  'fatigue': 'general medicine',
  'nausea': 'gastroenterology',
  'sore throat': 'general medicine'
};

export const useSearch = () => {
  const {
    searchQuery,
    searchCategory,
    searchResults,
    isSearching,
    setSearchQuery,
    setSearchCategory,
    setSearchResults,
    setIsSearching,
    setSearchSuggestions,
    resetSearch
  } = useAppStore();

  const [filteredSuggestions, setFilteredSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);

  // Filter suggestions based on query
  useEffect(() => {
    if (searchQuery.length > 1) {
      const filtered = searchSuggestions.filter(suggestion =>
        suggestion.toLowerCase().includes(searchQuery.toLowerCase())
      );
      setFilteredSuggestions(filtered);
      setSearchSuggestions(filtered);
      setShowSuggestions(true);
    } else {
      setFilteredSuggestions([]);
      setShowSuggestions(false);
    }
  }, [searchQuery, setSearchSuggestions]);

  // Search query with React Query
  const {
    isLoading,
    error,
    refetch
  } = useQuery(
    ['search', searchQuery, searchCategory],
    () => performSearch(searchQuery, searchCategory),
    {
      enabled: searchQuery.length > 2,
      staleTime: 5 * 60 * 1000, // 5 minutes
      cacheTime: 10 * 60 * 1000, // 10 minutes
    }
  );

  // Perform search based on category
  const performSearch = useCallback(async (query, category) => {
    if (!query || query.length < 2) return null;

    setIsSearching(true);
    
    try {
      let results;
      
      switch (category) {
        case 'symptoms':
          results = await searchAPI.searchBySymptoms([query]);
          break;
        case 'specialty':
          results = await searchAPI.searchBySpecialty(query);
          break;
        case 'doctor':
          results = await searchAPI.searchDoctors(query, { type: 'doctor' });
          break;
        case 'location':
          results = await searchAPI.searchByLocation(query);
          break;
        default:
          results = await searchAPI.searchDoctors(query);
      }
      
      setSearchResults(results);
      return results;
    } catch (error) {
      console.error('Search error:', error);
      throw error;
    } finally {
      setIsSearching(false);
    }
  }, [setIsSearching, setSearchResults]);

  // Handle search input change
  const handleSearchChange = useCallback((value) => {
    setSearchQuery(value);
    setShowSuggestions(value.length > 1);
  }, [setSearchQuery]);

  // Handle suggestion selection
  const handleSuggestionSelect = useCallback((suggestion) => {
    setSearchQuery(suggestion);
    setShowSuggestions(false);
    performSearch(suggestion, searchCategory);
  }, [setSearchQuery, searchCategory, performSearch]);

  // Handle category change
  const handleCategoryChange = useCallback((category) => {
    setSearchCategory(category);
    if (searchQuery) {
      performSearch(searchQuery, category);
    }
  }, [setSearchCategory, searchQuery, performSearch]);

  // Get specialty from symptom
  const getSpecialtyFromSymptom = useCallback((symptom) => {
    return symptomToSpecialty[symptom.toLowerCase()] || 'general medicine';
  }, []);

  // Clear search
  const clearSearch = useCallback(() => {
    resetSearch();
    setShowSuggestions(false);
  }, [resetSearch]);

  return {
    // State
    searchQuery,
    searchCategory,
    searchResults,
    isSearching,
    filteredSuggestions,
    showSuggestions,
    isLoading,
    error,
    
    // Actions
    handleSearchChange,
    handleSuggestionSelect,
    handleCategoryChange,
    performSearch,
    clearSearch,
    refetch,
    
    // Utilities
    getSpecialtyFromSymptom
  };
};
